import { StudyNumberInput } from './StudyNumberInput';

type DirectInputRange = {
  min: number;
  max: number;
};

/** A labelled range slider with a directly editable number. */
export function ToolSlider({
  label,
  value,
  min,
  max,
  step,
  display,
  testId,
  accent = 'accent-brand-coral',
  directInput,
  excludeZero = false,
  integer = false,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  testId?: string;
  /** Tailwind `accent-*` class from the design tokens. */
  accent?: string;
  directInput?: DirectInputRange;
  excludeZero?: boolean;
  integer?: boolean;
  onChange: (value: number) => void;
}) {
  const rangeMin = Math.min(min, value);
  const rangeMax = Math.max(max, value);
  // Keep the thumb aligned with precise typed values between the usual slider steps.
  const intervals = (value - rangeMin) / step;
  const rangeStep = Math.abs(intervals - Math.round(intervals)) < 1e-8 ? step : 'any';

  return (
    <div className="tool-slider block">
      <span className="flex items-baseline justify-between text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
        {label}
        <span className="flex min-w-0 flex-col items-end gap-1">
          <StudyNumberInput
            integer={integer}
            excludeZero={excludeZero}
            value={value}
            min={directInput?.min ?? min}
            max={directInput?.max ?? max}
            step={step}
            onValueChange={onChange}
            aria-label={`${label} number`}
            className="min-h-12 w-28 rounded-xl border border-hairline bg-canvas-pure px-3 py-2 text-right text-base font-extrabold normal-case tracking-normal text-ink tabular-nums focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
            data-testid={directInput ? testId : undefined}
          />
          <span
            className="tool-slider-value text-sm normal-case tracking-normal text-ink tabular-nums"
            data-testid={!directInput ? testId : undefined}
          >
            {display}
          </span>
        </span>
      </span>
      <input
        type="range"
        min={rangeMin}
        max={rangeMax}
        step={rangeStep}
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!excludeZero || next !== 0) onChange(next);
        }}
        aria-label={label}
        className={`mt-2 min-h-11 w-full ${accent}`}
      />
      <span className="block text-xs font-normal text-slate2">
        Range: {directInput?.min ?? min} to {directInput?.max ?? max}
        {excludeZero ? '; choose a non-zero value' : ''}
      </span>
    </div>
  );
}
